import React, { useState, useCallback, useEffect } from 'react';
import { Form, Input, Button, message, Tooltip } from 'antd';
import { File, LucideInfo, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import dayjs from 'dayjs';




const AutomaticDataEntryForClientList = ({ onAddRow, onDataSubmitted, onClose, onComplete }) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(async (values) => {
    setIsSubmitting(true);
    try {
      const userId = localStorage.getItem('userid');
      const year = dayjs().year();

      const response = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/clients/${year}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user-id': userId,
        },
        body: JSON.stringify(values)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      message.success(data.message || 'Client data submitted successfully');
      
      form.resetFields();

      if (onAddRow) {
        onAddRow({
          'Company Name': values.company_name,
          'Plan Sponsor Contact Info': values.plan_sponsor_contact_info,
          'Contract Number': values.contract_number,
          'Recordkeeper': values.record_keeper_name,
          'Recordkeeper Contact Info': values.recordkeeper_contact_info,
          'TPA': values.tpa_name,
          'TPA Contact Info': values.tpa_contact_info,
          'Company Administrators': values.company_administrator,
          'Advisor / Plan Manager': values.advisor_or_plan_manager,
          'Notes': values.notes
        });
      }

      if (onDataSubmitted) {
        onDataSubmitted();
      }
      
      // Close the modal after successful submission
      if (onClose) {
        onClose();
      }
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('Error submitting client data:', error);
      message.error('Failed to submit client data');
    } finally {
      setIsSubmitting(false);
    }
  }, [form, onAddRow, onDataSubmitted, onClose, onComplete]);

  const renderLabel = (label, tooltip) => {
    const labelText = label.replace(' *', '');
    const isRequired = label.includes('*');
    
    return (
      <div className="flex items-center">
        <span>
          {labelText}
          {isRequired && <span className="text-red-500 ml-0.5">*</span>}
        </span>
        <Tooltip title={tooltip}>
          <LucideInfo className="text-gray-400 hover:text-gray-600 ml-2 h-4 w-4" />
        </Tooltip>
      </div>
    );
  };

  useEffect(() => {
    form.resetFields();
  }, [form]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className="max-w-7xl mx-auto p-6 bg-white rounded-2xl shadow-lg"
    >
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-light text-gray-800">Client Data Entry</h1>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X size={24} className="text-gray-600" />
          </button>
        )}
      </div>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="space-y-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Form.Item
            name="company_name"
            label={renderLabel("Company Name *", "Enter the full legal name of the client company.")}
            rules={[{ required: true, message: 'Please enter company name' }]}
          >
            <Input placeholder="Enter company name" className="rounded-lg" />
          </Form.Item>
          <Form.Item
            name="plan_sponsor_contact_info"
            label={renderLabel("Plan Sponsor Contact Info", "Contact information for the plan sponsor.")}
          >
            <Input placeholder="Enter plan sponsor contact info" className="rounded-lg" />
          </Form.Item>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Form.Item
            name="record_keeper_name"
            label={renderLabel("Recordkeeper *", "The institution that maintains the plan's records.")}
            rules={[{ required: true, message: 'Please enter recordkeeper' }]}
          >
            <Input placeholder="Enter Recordkeeper" className="rounded-lg" />
          </Form.Item>
          <Form.Item
            name="recordkeeper_contact_info"
            label={renderLabel("Recordkeeper Contact Info", "Contact information for the recordkeeper.")}
          >
            <Input placeholder="Enter recordkeeper contact info" className="rounded-lg" />
          </Form.Item>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Form.Item
            name="tpa_name"
            label={renderLabel("TPA *", "Third Party Administrator")}
            rules={[{ required: true, message: 'Please enter TPA' }]}
          >
            <Input placeholder="Enter TPA" className="rounded-lg" />
          </Form.Item>
          <Form.Item
            name="tpa_contact_info"
            label={renderLabel("TPA Contact Info", "Contact information for the TPA.")}
          >
            <Input placeholder="Enter TPA contact info" className="rounded-lg" />
          </Form.Item>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Form.Item
            name="contract_number"
            label={renderLabel("Contract Number", "The unique identifier assigned to this plan.")}
          >
            <Input placeholder="Enter contract number" className="rounded-lg" />
          </Form.Item>
          <Form.Item
            name="company_administrator"
            label={renderLabel("Company Administrator *", "The primary contact person at the company.")}
            rules={[{ required: true, message: 'Please enter company administrator' }]}
          >
            <Input placeholder="Enter company administrator" className="rounded-lg" />
          </Form.Item>
        </div>

        <Form.Item
          name="advisor_or_plan_manager"
          label={renderLabel("Advisor / Plan Manager *", "The financial advisor or manager for this plan.")}
          rules={[{ required: true, message: 'Please enter advisor or plan manager' }]}
        >
          <Input placeholder="Enter advisor or plan manager" className="rounded-lg" />
        </Form.Item>

        <Form.Item
          name="notes"
          label={renderLabel("Notes", "Additional notes or comments about the client.")}
        >
          <Input.TextArea
            placeholder="Enter any additional notes"
            rows={4}
            className="rounded-lg"
          />
        </Form.Item>

        <div className="flex justify-start">
          <Tooltip title="Save this client's information to the database">
            <Button 
              htmlType="submit"
              loading={isSubmitting}
              size="large"
              className="inline-flex items-center px-6 py-3 border border-transparent text-lg font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out"
            >
              <File className="h-5 w-5 mr-2" />
              Submit Client Data
            </Button>
          </Tooltip>
        </div>
      </Form>
    </motion.div>
  );
};

export default AutomaticDataEntryForClientList;
